import { useState, useEffect, useMemo } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ShoppingCart, 
  Package, 
  Users, 
  TrendingUp, 
  Loader2,
  Truck,
  Bell,
  ArrowUpRight,
  Sparkles,
  AlertCircle,
  RefreshCw,
  Clock,
  MessageSquare,
  Zap,
  Activity
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  TrendingUp,
  ShoppingCart,
  Users,
  Truck,
  Package,
  Sparkles,
  MessageSquare,
  Zap
};

const TenantDashboardPage = () => {
  const { tenant, tenantSlug } = useTenant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Colores dinámicos basados en el tenant para un look premium
  const primaryColor = tenant?.primary_color || '#06b6d4';
  const glassBg = "bg-white/5 backdrop-blur-xl border-white/10";

  useEffect(() => {
    if (tenantSlug) {
      fetchDashboardData();
    }
  }, [tenantSlug]);

  const fetchDashboardData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      // Usamos el slug para que la ruta sea más predecible y robusta
      const response = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/dashboard-metrics`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-6">
        <div className="relative">
            <Loader2 className="w-16 h-16 animate-spin text-blue-500 opacity-20" />
            <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-blue-400 animate-pulse" />
            </div>
        </div>
        <div className="text-center">
            <h3 className="text-white font-bold text-lg">Sincronizando Silos</h3>
            <p className="text-slate-400 text-sm mt-1 animate-pulse font-medium">Inyectando inteligencia de {tenant?.name}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10" data-testid="tenant-dashboard">
      {/* Dynamic Header section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.5)] animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-emerald-500/80">Operación en Vivo</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black font-outfit tracking-tight text-white drop-shadow-2xl">
            Métricas <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/50" style={{ backgroundImage: `linear-gradient(to right, #fff, ${primaryColor})` }}>Pro</span>
          </h1>
          <p className="text-slate-400 mt-2 font-medium italic opacity-80 text-sm">
            Panel inteligente de gestión operativa para {tenant?.name}
          </p>
        </motion.div>

        <div className="flex items-center gap-3">
            <button 
               onClick={() => fetchDashboardData(true)}
               disabled={refreshing}
               className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 text-xs font-black transition-all text-slate-300 hover:text-white"
            >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Sincronizando...' : 'Refrescar'}
            </button>
            <button className="px-5 py-2.5 rounded-2xl bg-white text-black text-xs font-black shadow-xl hover:-translate-y-1 transition-all">
                Exportar Reporte
            </button>
        </div>
      </div>

      {/* Alerts/Notifications section */}
      <AnimatePresence mode="popLayout">
        {data?.alerts?.map((alert) => (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`p-5 rounded-3xl flex items-start gap-4 border shadow-2xl ${
              alert.type === 'warning' ? 'bg-amber-500/10 border-amber-500/20 text-amber-500' : 'bg-blue-500/10 border-blue-500/20 text-blue-500'
            }`}
          >
            <div className={`p-2 rounded-xl ${alert.type === 'warning' ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                <AlertCircle className="w-5 h-5" />
            </div>
            <div className="flex-1 text-sm font-bold">{alert.message}</div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {data?.summary?.map((stat, index) => {
          const IconComp = iconMap[stat.icon] || TrendingUp;
          return (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <Card className={`${glassBg} hover:border-white/30 transition-all duration-500 group overflow-hidden relative border shadow-2xl`}>
                {stat.is_mock && (
                    <div className="absolute top-2 left-3 px-2 py-0.5 rounded-full bg-white/10 text-[7px] font-black text-white/50 uppercase tracking-widest border border-white/5 z-20">
                        Proyección
                    </div>
                )}
                <div className="absolute top-0 right-0 p-6 opacity-[0.03] group-hover:opacity-10 group-hover:scale-150 transition-all duration-700">
                    <IconComp className="w-20 h-20" />
                </div>
                <CardContent className="pt-8 pb-7">
                  <div className="flex items-center justify-between relative z-10">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">{stat.label}</p>
                      <p className="text-4xl font-black text-white tracking-tight leading-none">{stat.value}</p>
                      <div className="flex items-center gap-1.5 mt-4 text-[11px] font-bold text-emerald-400 group-hover:translate-x-1 transition-transform">
                          <Zap className="w-3.5 h-3.5" />
                          <span>{stat.count || 0} hoy</span>
                      </div>
                    </div>
                    <div className={`p-5 rounded-3xl bg-white/5 border border-white/5 group-hover:shadow-[0_0_20px_rgba(255,255,255,0.05)] transition-all duration-500`}>
                      <IconComp className={`w-8 h-8 ${stat.color} filter drop-shadow-[0_0_8px_currentColor]`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Operations Analysis Center */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Operational Chart Card */}
        <Card className={`${glassBg} lg:col-span-2 overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/5`}>
          <CardHeader className="border-b border-white/5 pb-8 pt-8 px-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 flex items-center gap-3">
                <Activity className="w-4 h-4 text-emerald-400" />
                Flujo Operativo 7D
              </CardTitle>
              <div className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/5 text-[10px] font-black text-slate-400 uppercase">
                Volumen Estimado por Nodo
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-10 px-6 pb-6 text-white">
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.charts?.main}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={primaryColor} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={primaryColor} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.03)" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fill: '#64748b', fontSize: 11, fontWeight: 800}}
                    dy={15}
                  />
                  <YAxis hide />
                  <Tooltip 
                    contentStyle={{ 
                        backgroundColor: 'rgba(15, 23, 42, 0.95)', 
                        borderColor: 'rgba(255,255,255,0.1)', 
                        borderRadius: '20px',
                        fontWeight: 'bold'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke={primaryColor} 
                    strokeWidth={4}
                    fill="url(#colorValue)" 
                    animationDuration={2000}
                    dot={{ r: 4, fill: '#0f172a', stroke: primaryColor, strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Channels/Segmentation Chart */}
        <Card className={`${glassBg} overflow-hidden shadow-2xl border border-white/5`}>
            <CardHeader className="pt-8 px-8 pb-2">
                <CardTitle className="text-xs font-black uppercase tracking-[0.3em] text-slate-400">Segmentación Operativa</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center p-8">
                <div className="h-[220px] w-full relative">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data?.charts?.secondary}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={85}
                                paddingAngle={5}
                                dataKey="value"
                            >
                                {data?.charts?.secondary?.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-2xl font-black text-white">100%</span>
                        <span className="text-[7px] font-black text-slate-600 uppercase tracking-widest">Actividad</span>
                    </div>
                </div>
                <div className="w-full space-y-3 mt-8">
                    {data?.charts?.secondary?.map((item, i) => (
                        <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{item.name}</span>
                            </div>
                            <span className="text-xs font-black text-white">{item.value}%</span>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
      </div>

      {/* Lower Section: Timeline & Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-10">
        {/* Activity Timeline */}
        <Card className={`${glassBg} border-white/10 shadow-2xl`}>
             <CardHeader className="pt-8 px-8 border-b border-white/5 pb-6">
                <CardTitle className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 flex items-center gap-3">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    Historial Reciente
                </CardTitle>
             </CardHeader>
             <CardContent className="px-8 pt-8">
                <div className="space-y-8 relative">
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-white/5" />
                    {data?.timeline?.map((event) => (
                        <div key={event.id} className="relative pl-10">
                            <div className={`absolute left-0 top-1 w-6 h-6 rounded-lg border border-white/10 flex items-center justify-center z-10 ${
                                event.type === 'sale' ? 'bg-emerald-500/20 text-emerald-400' : 
                                event.type === 'bot' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                            }`}>
                                {event.type === 'sale' ? <ShoppingCart className="w-3 h-3" /> : 
                                 event.type === 'bot' ? <MessageSquare className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-black text-slate-200">{event.title}</p>
                                    <span className="text-[9px] font-black text-slate-600 uppercase tracking-tighter">{event.time}</span>
                                </div>
                                <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">{event.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
             </CardContent>
        </Card>

        {/* Matrix Action Card */}
        <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button className="flex flex-col items-start p-10 rounded-3xl bg-white/5 border border-white/5 hover:bg-emerald-500/10 hover:border-emerald-500/20 transition-all group overflow-hidden relative shadow-2xl min-h-[220px]">
                    <div className="absolute top-0 right-0 p-10 opacity-[0.02] group-hover:opacity-10 transition-opacity">
                        <ShoppingCart className="w-32 h-32" />
                    </div>
                    <div className="p-4 rounded-2xl bg-emerald-500/10 text-emerald-400 mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all">
                        <ShoppingCart className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-black text-white tracking-tighter">Nueva Venta</h3>
                    <p className="text-slate-500 text-sm mt-3 font-medium text-left">Genera un flujo de salida y ticket en menos de 20 segundos.</p>
                </button>

                <button className="flex flex-col items-start p-10 rounded-3xl bg-white/5 border border-white/5 hover:bg-blue-500/10 hover:border-blue-500/20 transition-all group overflow-hidden relative shadow-2xl min-h-[220px]">
                    <div className="absolute top-0 right-0 p-10 opacity-[0.02] group-hover:opacity-10 transition-opacity">
                        <Package className="w-32 h-32" />
                    </div>
                    <div className="p-4 rounded-2xl bg-blue-500/10 text-blue-400 mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all">
                        <Package className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-black text-white tracking-tighter">Inventario Silo</h3>
                    <p className="text-slate-500 text-sm mt-3 font-medium text-left">Control centralizado de almacenes en multinodo.</p>
                </button>
            </div>

            <Card className="bg-gradient-to-br from-indigo-600 via-blue-700 to-cyan-600 border-0 overflow-hidden relative group shadow-[0_20px_50px_rgba(59,130,246,0.2)]">
                <div className="absolute top-0 right-0 p-16 opacity-10 -mr-10 -mt-10 group-hover:scale-150 transition-all duration-1000">
                    <Sparkles className="w-40 h-40 text-white" />
                </div>
                <CardContent className="p-10 relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 h-full min-h-[200px]">
                    <div className="flex-1">
                        <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-[9px] font-black text-white uppercase tracking-[0.3em] border border-white/20 w-fit mb-4">Enterprise Intelligence</div>
                        <h3 className="text-3xl font-black text-white tracking-tighter leading-tight">Analítica con <br/>Fluxo Maestro</h3>
                        <p className="text-white/70 text-sm mt-4 font-medium max-w-md">Nuestra IA analiza tus procesos en tiempo real para sugerirte optimizaciones de costos y rutas de entrega.</p>
                    </div>
                    <button className="px-10 py-5 rounded-3xl bg-white text-blue-700 font-black text-sm hover:shadow-2xl hover:-translate-y-1 transition-all uppercase tracking-widest active:scale-95">
                        Consultar IA
                    </button>
                </CardContent>
            </Card>
        </div>
      </div>
    </div>
  );
};

export default TenantDashboardPage;
